/**
 * Round 9 C4 -- the accountable person answers.
 *
 * The incident page reached from his own bell named him, asked nothing
 * of him and carried no control of any kind, for three rounds. He is
 * the one person who knows what the customers are seeing and the only
 * one with nowhere to say it.
 *
 * Not workflow. The lean Finding shape is a P2 lock and the human
 * workflow lives on the CAP that responds; this is an append-only note
 * beside the record, naming who said it and when.
 */
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api } from "shared/api/client";
import { useToast } from "shared/toast/ToastProvider";
import { formatDateTime } from "shared/time/formatDate";

interface FindingComment {
  id: string;
  author_name: string;
  body: string;
  created_at: string;
}

interface FindingCommentCreated extends FindingComment {
  /** Round 10 C1: how many people were actually told. */
  notified: number;
}

export function FindingCommentBox({
  findingId,
  audience,
  audienceNames = [],
  internal = false,
  change = false,
}: {
  findingId: string;
  /** How many people will be told. Zero is a real answer. */
  audience: number;
  /** Round 20 U2 -- the same people by name, so the reader knows who. */
  audienceNames?: string[];
  /** Round 16 L4 -- the subject is a Critical Internal Function, which
   *  has no customers to ask about. */
  internal?: boolean;
  /** Round 19 G35 -- a finding a planned change raised: not an incident,
   *  so no customer question and no incident record. */
  change?: boolean;
}) {
  const placeholderKey = change
    ? "modules.twin.finding.commentPlaceholderChange"
    : internal
      ? "modules.twin.finding.commentPlaceholderInternal"
      : "modules.twin.finding.commentPlaceholder";
  const { t, i18n } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();
  const [body, setBody] = useState("");

  const key = ["finding-comments", findingId] as const;
  const comments = useQuery({
    queryKey: key,
    queryFn: () => api.get<FindingComment[]>(`/v1/findings/${findingId}/comments`),
  });
  const add = useMutation({
    mutationFn: (text: string) =>
      api.post<FindingCommentCreated>(`/v1/findings/${findingId}/comments`, {
        body: text,
      }),
    onSuccess: (created) => {
      setBody("");
      void qc.invalidateQueries({ queryKey: key });
      /*
       * Round 10 C1. "Added to the incident record." says the record
       * took it and nothing about whether a person will read it -- the
       * man accountable for a service in a live critical incident had
       * been given somewhere to speak and no evidence anyone was
       * listening.
       *
       * The count comes from the server, so the toast can only claim an
       * audience that exists. Nobody reflected on the desk yet is a
       * real state on a fresh estate (the B1 posture) and it is said
       * plainly rather than dressed up as a delivery.
       */
      toast.push({
        tone: created.notified > 0 ? "success" : "warning",
        message:
          created.notified > 0
            ? t("modules.twin.finding.commentSent", { count: created.notified })
            : t("modules.twin.finding.commentSentNobody"),
      });
    },
  });

  return (
    <div className="mt-2 space-y-2">
      {/* Said BEFORE he types, because "who reads this" is the question
          that decides whether it is worth writing. */}
      <p
        data-testid="finding-comment-audience"
        data-count={audience}
        className="text-xs text-slate-600"
      >
        {audienceNames.length > 0
          ? t("modules.twin.finding.commentAudienceNamed", {
              names: audienceNames.join(", "),
            })
          : audience > 0
            ? t("modules.twin.finding.commentAudience", { count: audience })
            : t("modules.twin.finding.commentAudienceNobody")}
      </p>
      <label className="block">
        <span className="sr-only">{t(placeholderKey)}</span>
        <textarea
          dir="auto"
          data-testid="finding-comment-input"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={2}
          placeholder={t(placeholderKey)}
          className="w-full rounded border border-slate-300 px-2 py-1 text-xs"
        />
      </label>
      <button
        type="button"
        data-testid="finding-comment-submit"
        disabled={!body.trim() || add.isPending}
        onClick={() => add.mutate(body.trim())}
        /* Round 17 H33 -- the incident's one action measured 165 by 24
           pixels on a phone; a 44-pixel target there, as before on a
           desk. */
        className="min-h-[44px] rounded bg-brand-brass px-3 py-1 text-xs sm:min-h-0 font-medium text-brand-navy hover:bg-brand-brassDark disabled:opacity-50"
      >
        {t(
          change
            ? "modules.twin.finding.commentSubmitChange"
            : "modules.twin.finding.commentSubmit",
        )}
      </button>

      <div data-testid="finding-comments">
        <p className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
          {t("modules.twin.finding.commentsHeading")}
        </p>
        {comments.data && comments.data.length > 0 ? (
          <ul className="mt-1 space-y-1">
            {comments.data.map((c) => (
              <li key={c.id} className="text-xs text-slate-700">
                <span className="font-medium">{c.author_name}</span>
                <span className="text-slate-400">
                  {" · "}
                  {formatDateTime(c.created_at, i18n.language)}
                </span>
                <p className="whitespace-pre-wrap">{c.body}</p>
              </li>
            ))}
          </ul>
        ) : (
          <p className="mt-1 text-xs italic text-slate-500">
            {t("modules.twin.finding.commentsEmpty")}
          </p>
        )}
      </div>
    </div>
  );
}
